/* eslint-disable complexity */
/* eslint-disable max-statements */
import React, { useState, useEffect } from "react";
import loadable from "@loadable/component";
import styles from "./styles.css";
import PropTypes from "prop-types";
import DriveAwayPriceModal from "../drive-away-price-modal/component";
import PlatinumCoverPlans from "../platinum-cover-plans";
import { getCheckoutPageURL } from "../../../utils/helpers/get-detail-page-url";
import { useHistory } from "react-router";
import { CHECKOUT_STEPS, CHECKOUT_STEPS_STATUS, VAS_CATEGORY_KEYS } from "../../../constants/checkout-constants";
import { SCREEN_TYPE } from "../add-ons/reducers";
import { NUMBER } from "../../../constants/app-constants";
import CustomiseYourCoverModal from "../customise-your-cover-modal/component";
import { checkoutRoutes } from "../../../constants/checkout-routes";
import { PLATINUM_COVER_GA_EVENTS } from "./tracking";
import TrackImpression from "../../shared/track-impression";
import { trackMobileCustomEventsAU } from "../../../tracking";
import { getIsNewServicingVariant } from "../../../utils/statsig-ab-utils/get-experiment-data";
import LoaderScreen from "../loader-screen/component";
import { yieldToMain } from "../../../utils/helpers/yield-to-main";
import { TopBundlePitch, BottomBundlePitch } from "./bundle-pitch/component";
import { TopServicePitch, BottomServicePitch } from "./service-pitch/component";
import { TopWarrantyPitch, BottomWarrantyPitch } from "./warranty-pitch/component";
import WarrantyPitchingOurTrackRecord from "../warranty-pitching-our-track-record/component";
import CarServicingDidYouKnowCard from "../../shared/cars24-cover-pitching/car-servicing-did-you-know-card";
import Button from "../../shared/button";
import { makePriceLabelRound } from "../../../utils/helpers/make-price-label";
import BestInAustralia from "../../shared/cars24-cover-pitching/best-in-australia/component";
import ArrowIcon from "../../shared/cars24-cover-pitching/manufacturer-level-coverage/images/arrow.svg";
const WarrantyModal = loadable(() => import("../post-payment-journey/add-ons-plans/warranty-modal/component"));
import MechanicCard from "../../shared/cars24-cover-pitching/mechanic-card/component";
const PitchingSkipModal = loadable(() => import("../pitching-skip-modal"));
import CountUp from "react-countup";

const getTopPitchContent = (screenType, props = {}) => {
    switch (screenType) {
    case VAS_CATEGORY_KEYS.WARRANTY:
        return <TopWarrantyPitch {...props} />;
    case VAS_CATEGORY_KEYS.CAR_SERVICING:
        return <TopServicePitch {...props} />;
    case VAS_CATEGORY_KEYS.CAR_COVER_BUNDLES:
        return <TopBundlePitch {...props} />;
    default:
        return null;
    }
};

const getBottomPitchContent = (screenType, props = {}) => {
    switch (screenType) {
    case VAS_CATEGORY_KEYS.WARRANTY:
        return <BottomWarrantyPitch {...props} />;
    case VAS_CATEGORY_KEYS.CAR_SERVICING:
        return <BottomServicePitch {...props} />;
    case VAS_CATEGORY_KEYS.CAR_COVER_BUNDLES:
        return <BottomBundlePitch {...props} />;
    default:
        return null;
    }
};

const servicingOnlyDisclaimer = `
    Service focuses on maintenance and detailed inspection of your car & doesn’t include all logbook requirements. 
    Additional requirements for a logbook service will be quoted separately by your service advisor, as necessary.
    `;

const warrantyOnlyDisclaimer = `
    CARS24 Cover™ is a suite of products designed to offer you various protection for your vehicle. 
    Some products may be offered as an insurance policy and some may not be. Please always read the relevant terms and conditions before purchasing. 
    Based on all claims received by CARS24 over a 3 year period from 2021 to 2023, excluding issues that were serviceable or FW&T in nature.
    `;

const warrantyPlusServiceDisclaimer = `
    CARS24 Cover™ is a suite of products designed to offer you various protection for your vehicle. 
    Some products may be offered as an insurance policy and some may not be. 
    Service focuses on maintenance and detailed inspection of your car & doesn’t include all logbook requirements. 
    Additional requirements for a logbook service will be quoted separately by your service advisor, as necessary.
    Please always read the relevant terms and conditions before purchasing. 
    Based on all claims received by CARS24 over a 3 year period from 2021 to 2023, excluding issues that were serviceable or FW&T in nature.
    `;

const getDisclaimer = (screenType) => {
    switch (screenType) {
    case VAS_CATEGORY_KEYS.CAR_SERVICING:
        return servicingOnlyDisclaimer;
    case VAS_CATEGORY_KEYS.WARRANTY:
        return warrantyOnlyDisclaimer;
    case VAS_CATEGORY_KEYS.CAR_COVER_BUNDLES:
        return warrantyPlusServiceDisclaimer;
    default:
        return "CARS24 Cover™ is a suite of products designed to offer you various protection for your vehicle. Some products may be offered as an insurance policy and some may not be.  Please always read the relevant terms and conditions before purchasing.";
    }
};

const DisclaimerSection = ({ personalised, interestRate, comparisonRate, screenType  }) => {
    return (
        <React.Fragment>
            <p styleName={"styles.text"}>
                {getDisclaimer(screenType)}
            </p>
            <p styleName={"styles.text"}>
                {personalised
                    ? "Weekly payments are based on the below rates received during your pre-approval and are subject to change."
                    : "Weekly repayments calculated based on"}
                <br />
                {interestRate.toFixed(NUMBER.TWO)}% interest rate (p.a) {!personalised && " and"}
                <br />
                {comparisonRate.toFixed(NUMBER.TWO)}% comparison rate
            </p>
        </React.Fragment>
    );
};

DisclaimerSection.propTypes = {
    personalised: PropTypes.bool,
    interestRate: PropTypes.number,
    comparisonRate: PropTypes.number,
    screenType: PropTypes.string
};

const Cars24CoverPitching = ({
    routeData,
    content,
    order = {},
    vasCartData,
    getCarCoverBundleConnect,
    servicingPriceConfig,
    clearVASCartConnect,
    setAddonScreenTypeConnect,
    updateCheckoutStepsConnect,
    carCoverData,
    financeFirstCheckoutEnabled,
    removeVASItemConnect,
    updateLoanQuoteConnect,
    setCounterAnimationConnect,
    selfPickUp,
    getVASCartConnect,
    screenType = "EXTENDED_WARRANTY_PLAN",
    paymentSummaryData,
    getPaymentSummaryConnect,
    showCounterAnimation
}) => {
    const { egc: egcPrice, appointmentId, year } = content || {};
    const { tradeinOpted, orderId } = order;
    const vasCartItems = (vasCartData || {}).items || [];
    const vasCartProducts = vasCartItems.filter(
        (item) =>
            item.categoryKey === VAS_CATEGORY_KEYS.WARRANTY ||
            item.categoryKey === VAS_CATEGORY_KEYS.CAR_SERVICING ||
            item.categoryKey === VAS_CATEGORY_KEYS.CAR_COVER_BUNDLES
    );

    const showItemAddedFooter = vasCartItems.find(item => (item.categoryKey === screenType || screenType === VAS_CATEGORY_KEYS.CAR_COVER_BUNDLES));

    const servicingLocation = selfPickUp?.[0]?.pickUpLocation?.locationAddress;
    const carCoverDetails = carCoverData?.carCoverDetails;
    const { interestRate = 0, comparisonRate = 0, personalised = false, carCoverMetadata } = carCoverDetails || {};
    const { skipCtaText } = carCoverMetadata || {};
    const [showDapModal, setShowDapModal] = useState(false);
    const [showWhatsCoveredModal, setShowWhatsCoveredModal] = useState(false);
    const [showCustomiseCoverModal, setShowCustomiseCoverModal] = useState(false);
    const [cartLoading, setCartLoading] = useState(false);
    const [bundleLoading, setBundleLoading] = useState(false);
    const history = useHistory();
    const [skipLoading, setSkipLoading] = useState(false);
    const [isSkipModalOpen, setIsSkipModalOpen] = useState(false);
    const showCartView = vasCartProducts.length > 0 && screenType === VAS_CATEGORY_KEYS.CAR_COVER_BUNDLES;

    const [isModalOpen, setIsModalOpen] = useState(null);

    const handleWhyUsModal = (tab) => {
        setIsModalOpen(tab);
    };

    const handleModal = () => {
        setShowDapModal(!showDapModal);
    };

    const handleWhatsCoveredModal = async () => {
        await yieldToMain();
        setShowWhatsCoveredModal(!showWhatsCoveredModal);
    };

    const fetchBundleData = async () => {
        setBundleLoading(true);
        await getCarCoverBundleConnect();
        setCounterAnimationConnect(false);
        setBundleLoading(false);
    };

    useEffect(() => {
        if (!servicingPriceConfig?.loading && servicingPriceConfig?.data?.experiment) {
            fetchBundleData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [servicingPriceConfig]);

    useEffect(() => {
        if (orderId) {
            getVASCartConnect({ orderId });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderId]);

    const stepData = {
        appointmentId: content?.appointmentId,
        step: CHECKOUT_STEPS.CARCOVER,
        state: CHECKOUT_STEPS_STATUS.COMPLETE
    };

    const navigateToNextScreen = (skipped) => {
        const isNewServicingVariant = getIsNewServicingVariant(servicingPriceConfig?.data?.experiment);
        const filteredItems = isNewServicingVariant
            ? vasCartItems.filter(
                (item) =>
                    item.categoryKey !== VAS_CATEGORY_KEYS.WARRANTY &&
                      item.categoryKey !== VAS_CATEGORY_KEYS.CAR_SERVICING &&
                      item.categoryKey !== VAS_CATEGORY_KEYS.CAR_COVER_BUNDLES
            )
            : vasCartItems;
        if (filteredItems.length > 0) setAddonScreenTypeConnect({ screenType: SCREEN_TYPE.CART_VIEW });
        else setAddonScreenTypeConnect({ screenType: SCREEN_TYPE.LANDING_PAGE });
        if (skipped) updateCheckoutStepsConnect({ ...stepData, state: CHECKOUT_STEPS_STATUS.SKIPPED });
        else updateCheckoutStepsConnect(stepData);
        const nextScreenUrl = financeFirstCheckoutEnabled
            ? checkoutRoutes.preApprovedAddOns.route
            : routeData.defaultNext;
        const { relativeURL: nextURL } = getCheckoutPageURL(content, nextScreenUrl);
        history.push(nextURL);
    };

    const handleSkip = async (fromSkipModal = false) => {
        const skipLanded = window.sessionStorage.getItem(`skipLanded_${orderId}`) === "true";
        if (!skipLanded && !fromSkipModal) {
            window.sessionStorage.setItem(`skipLanded_${orderId}`, "true");
            setIsSkipModalOpen(true);
            trackMobileCustomEventsAU("custom_event", {
                event: "custom_events",
                eventCategory: "Cars24_checkout_page",
                eventAction: "Cars24_cover_pitching_components_seen",
                eventLabel: "Exit_pop_up"
            });
        } else {
            setSkipLoading(true);
            await clearVASCartConnect(orderId, "CAR24_COVER");
            getPaymentSummaryConnect();
            setSkipLoading(false);
            navigateToNextScreen(true);
        }
    };

    const onItemRemove = async (product) => {
        const { key: productKey, type: productType } = product;
        setCartLoading(true);
        await removeVASItemConnect({
            appointmentId,
            key: productKey,
            type: productType,
            orderId,
            year,
            price: egcPrice,
            vasPageName: "CAR24_COVER"
        });
        getPaymentSummaryConnect();
        setCartLoading(false);
        if (financeFirstCheckoutEnabled) {
            await updateLoanQuoteConnect(orderId);
        }
        setCounterAnimationConnect(true);
        const gaEventLabel = PLATINUM_COVER_GA_EVENTS.CART_PAGE_PRODUCT_REMOVED.eventLabel
            .replace("$product", productKey)
            .replace("$duration", "months");
        trackMobileCustomEventsAU("", {
            ...PLATINUM_COVER_GA_EVENTS.CART_PAGE_PRODUCT_REMOVED,
            eventLabel: gaEventLabel
        });
    };

    // eslint-disable-next-line no-unused-vars
    const onProceedCart = () => {
        navigateToNextScreen();
        trackMobileCustomEventsAU(PLATINUM_COVER_GA_EVENTS.Proceed_clicked_from_bottom_nav, {
            ...PLATINUM_COVER_GA_EVENTS.Proceed_clicked_from_bottom_nav
        });
    };

    useEffect(() => {
        if (vasCartProducts?.length === 0) {
            setShowCustomiseCoverModal(false);
        }
    }, [vasCartProducts]);

    if (bundleLoading) {
        return <LoaderScreen />;
    }

    return (
        <div styleName={"styles.Wrapperouter"}>
            <div styleName={"styles.middleContainer"}>
                {getTopPitchContent(screenType, { carCoverMetadata, servicingLocation })}
                <PlatinumCoverPlans
                    isPitchingScreen={true}
                    navigateToNextScreen={navigateToNextScreen}
                    handleWhatsCoveredModal={handleWhatsCoveredModal}
                    isFinanceFirst={financeFirstCheckoutEnabled}
                />
                {getBottomPitchContent(screenType, { carCoverMetadata, servicingLocation })}
                <div styleName={"styles.bottomWrapper"}>
                    {!financeFirstCheckoutEnabled && skipCtaText && (
                        <TrackImpression event={{ data: PLATINUM_COVER_GA_EVENTS.SKIP_BUTTONS_SEEN }}>
                            <button onClick={() => handleSkip()}>
                                {skipLoading ? `${skipCtaText}...` : skipCtaText}
                            </button>
                        </TrackImpression>
                    )}

                    <div styleName={"styles.disclaimerWrapper"}>
                        <DisclaimerSection
                            personalised={personalised}
                            interestRate={interestRate}
                            comparisonRate={comparisonRate}
                            screenType={screenType}
                        />
                    </div>
                </div>
                {showDapModal && <DriveAwayPriceModal onClose={handleModal} showPriceMinusTradeIn={tradeinOpted} />}
                {isSkipModalOpen && (
                    <PitchingSkipModal
                        isSkipModalOpen={isSkipModalOpen}
                        onSkip={() => handleSkip(true)}
                        onClose={() => setIsSkipModalOpen(false)}
                        carCoverMetadata={carCoverMetadata}
                        orderId={orderId}
                        appointmentId={appointmentId}
                        year={year}
                        egc={egcPrice}
                        screenType={screenType}
                    />
                )}
                {(vasCartProducts.length > 0) && showItemAddedFooter && (
                    <div styleName={"styles.buttonWrapper"}>
                        <div styleName={"styles.cartWrap"}>
                            <p styleName={"styles.item"}>
                                {showCartView ? (
                                    `${vasCartProducts.length} ${
                                        vasCartProducts.length === 1 ? "item added" : "items added"
                                    }`
                                ) : (
                                    <span>
                                        {showCounterAnimation ? (
                                            <CountUp
                                                duration={1}
                                                separator=","
                                                prefix="$"
                                                end={paymentSummaryData?.summary?.aggregatedItem?.amount}
                                            />
                                        ) : (
                                            makePriceLabelRound(paymentSummaryData?.summary?.aggregatedItem?.amount)
                                        )}
                                    </span>
                                )}
                            </p>
                            <div
                                onClick={() => {
                                    if (showCartView) {
                                        setShowCustomiseCoverModal(true);
                                    } else {
                                        setShowDapModal(true);
                                    }
                                }}
                            >
                                <p styleName={"styles.viewCart"}>{showCartView ? "View cart" : "Drive away price"}</p>
                            </div>
                        </div>
                        <Button onClick={onProceedCart} text="Proceed" />
                        <CustomiseYourCoverModal
                            loading={cartLoading}
                            vasCartItems={vasCartProducts}
                            isOpen={showCustomiseCoverModal}
                            onClose={() => setShowCustomiseCoverModal(false)}
                            onItemRemove={onItemRemove}
                        />
                    </div>
                )}
            </div>
            <div styleName={"styles.rightContainer"}>
                {screenType === VAS_CATEGORY_KEYS.CAR_COVER_BUNDLES && (
                    <React.Fragment>
                        {carCoverMetadata?.infoCard && (
                            <div styleName={"styles.didYouKnow"}>
                                <CarServicingDidYouKnowCard {...carCoverMetadata?.infoCard} />
                            </div>
                        )}
                        <div styleName={"styles.warrantyTrackWrapper"}>
                            <WarrantyPitchingOurTrackRecord promotionData={carCoverMetadata?.promotionData} />
                        </div>
                    </React.Fragment>
                )}

                {screenType === VAS_CATEGORY_KEYS.WARRANTY && carCoverMetadata?.specificationsDetail && (
                    <React.Fragment>
                        <BestInAustralia specificationsDetail={carCoverMetadata?.specificationsDetail} />

                        {/* Why we’re the best in the market Modal */}
                        <div styleName={"styles.whyUsButton"} onClick={() => handleWhyUsModal("MARKET_COMPARISON")}>
                            <button>Why we’re the best in the market?</button>
                            <img src={ArrowIcon} alt="Arrow Icon" />
                        </div>
                    </React.Fragment>
                )}

                {screenType === VAS_CATEGORY_KEYS.CAR_SERVICING && carCoverMetadata?.technicianDetails && (
                    <div styleName={"styles.mechanicCardWrapper"}>
                        <p styleName={"styles.mechanicCardHeading"}>CARS24’s world-class team</p>
                        <p styleName={"styles.mechanicCardSubHeading"}>Best auto mechanics across Australia.</p>
                        <MechanicCard technicianDetails={carCoverMetadata?.technicianDetails[0]} />
                    </div>
                )}
            </div>

            <WarrantyModal
                isOpen={isModalOpen}
                planKey={screenType}
                onClose={() => setIsModalOpen(false)}
                isWhatsCovered={isModalOpen === "WHATS_COVERED"}
                isPitching={true}
            />
        </div>
    );
};

Cars24CoverPitching.propTypes = {
    routeData: PropTypes.object,
    content: PropTypes.object,
    order: PropTypes.object,
    egcData: PropTypes.object,
    deliveryMode: PropTypes.string,
    pickupCharges: PropTypes.object,
    tradeInData: PropTypes.object,
    getCarCoverBundleConnect: PropTypes.func,
    servicingPriceConfig: PropTypes.object,
    carCoverData: PropTypes.object,
    updateWarrantyPlansConnect: PropTypes.func,
    updateCheckoutStepsConnect: PropTypes.func,
    setAddonScreenTypeConnect: PropTypes.func,
    vasCartData: PropTypes.object,
    clearVASCartConnect: PropTypes.func,
    updateLoanQuoteConnect: PropTypes.func,
    setCounterAnimationConnect: PropTypes.func,
    financeFirstCheckoutEnabled: PropTypes.bool,
    removeVASItemConnect: PropTypes.func,
    selfPickUp: PropTypes.array,
    deliveryData: PropTypes.object,
    getVASCartConnect: PropTypes.func,
    screenType: PropTypes.string,
    paymentSummaryData: PropTypes.object,
    getPaymentSummaryConnect: PropTypes.func,
    showCounterAnimation: PropTypes.bool
};

export default Cars24CoverPitching;
